import { settings as raySettings } from '@wework/ray-core';
import { logError, logWarning } from 'client/javascript/util/logger';
import { geoData } from 'client/javascript/geo';
import { trackInteraction, EventNameTypes, makeSegmentTrackCall } from 'client/javascript/analytics';
import {
  isUserCountryInWhitelistedCountriesList,
  showModule,
  isGeoConfiguredModule,
  GEO_MODULE_SELECTOR
} from 'client/javascript/util/geolocation_based_module_rendering/helpers';
import {
  SELECTORS as HERO_SELECTORS,
  CLASSES as HERO_CLASSES
} from 'client/javascript/pages/shared/sections/hero_container/hero';
import { scrollToContent } from 'client/javascript/util/scroll_to_content';
import { ROLLBAR_LOGGING_PREFIX } from 'client/javascript/pages/shared/sections/hero_container';

export const SELECTORS = {
  MODULE: '.browse-by-needs',
  MODULE_ITEM: '.browse-by-needs-card',
  MODULE_ITEM_DESCRIPTION: '.browse-by-needs-card__description-wrapper',
  MODULE_ITEM_ACCORDION_LABEL: '.browse-by-needs-card__accordion-label',
  MODULE_ITEM_ACCORDION_LABEL_CLOSE: '.browse-by-needs-card__accordion-label-close',
  MODULE_IMAGE: '.browse-by-needs-card__image',
  MODULE_ITEM_LINK: '.browse-by-needs-card__link',
  MODULE_ITEM_DESCRIPTION_LINK: '.browse-by-needs-card-description__link'
};
export const INTERACTION_LABELS = {
  MODULE_ITEM_ACCORDION_LABEL: 'Click - Expand',
  MODULE_ITEM_ACCORDION_LABEL_CLOSE: 'Click - Collapse'
};
const TRACKING_SELECTORS = {
  CLICK_INTERACTION: '[data-click-interaction-tracking-label]',
  CUSTOM_INTERACTION: '[data-custom-tracking-label]'
};
const AVAILABLE_CARD_COUNT = 4;

export const setupBrowseByNeedsAccordion = (cardDomNode) => {
  if (!cardDomNode) {
    return null;
  }

  try {
    cardDomNode.querySelector(SELECTORS.MODULE_ITEM_ACCORDION_LABEL).addEventListener('click', ({ target }) => {
      const { labelId } = target.dataset;
      const content = document.getElementById(labelId);
      setTimeout(() => scrollToContent(content), 500);
    });
  } catch (error) {
    logError(`${ROLLBAR_LOGGING_PREFIX} setupBrowseByNeedsAccordion failed: ${error.message}`, error);
  }
};

export function getImagesHtml(card) {
  try {
    const { slideIndex } = card.dataset;
    const imageDomNode = card.querySelector(SELECTORS.MODULE_IMAGE);
    const { desktopImage } = imageDomNode.dataset;

    return `<img class="${HERO_CLASSES.HERO_IMAGE}" ${desktopImage} alt="${imageDomNode.alt}" data-slide-index="${slideIndex}" />`;
  } catch (error) {
    logWarning(`${ROLLBAR_LOGGING_PREFIX} getImagesHtml failed: ${error.message}`, error);
    return '';
  }
}

function isDesktopBreakpoint() {
  return window.innerWidth >= raySettings.breakpointsInPixels.desktop;
}

function removePreviousActiveImage(heroImageDomNode) {
  const previousActiveImage = heroImageDomNode.querySelector(`.${HERO_CLASSES.HERO_IMAGE_ACTIVE}`);

  if (previousActiveImage) {
    previousActiveImage.classList.remove(HERO_CLASSES.HERO_IMAGE_ACTIVE);
  }
}

function showDefaultImage({ heroDefaultImageNode, heroImageDomNode }) {
  removePreviousActiveImage(heroImageDomNode);

  heroDefaultImageNode.classList.remove(HERO_CLASSES.HERO_IMAGE_HIDE);
}

function toggleImageSliderClasses({ heroDefaultImageNode, heroSlideImageNode, heroImageDomNode }) {
  const defaultImageNotHidden = !heroDefaultImageNode.classList.contains(HERO_CLASSES.HERO_IMAGE_HIDE);

  if (defaultImageNotHidden) {
    heroDefaultImageNode.classList.add(HERO_CLASSES.HERO_IMAGE_HIDE);
  }

  removePreviousActiveImage(heroImageDomNode);

  if (heroSlideImageNode) {
    heroSlideImageNode.classList.add(HERO_CLASSES.HERO_IMAGE_ACTIVE);
  }
}

function initImageSlider({ moduleDomNode, heroImageDomNode, card }) {
  const { slideIndex } = card.dataset;
  const heroDefaultImageNode = heroImageDomNode.querySelector(HERO_SELECTORS.MODULE_DEFAULT_IMAGE);
  const heroSlideImageNode = heroImageDomNode.querySelector(
    `${HERO_SELECTORS.MODULE_IMAGE}[data-slide-index="${slideIndex}"]`
  );

  card.addEventListener('mouseenter', () =>
    toggleImageSliderClasses({ heroDefaultImageNode, heroSlideImageNode, heroImageDomNode })
  );
  moduleDomNode.addEventListener('mouseleave', () => showDefaultImage({ heroDefaultImageNode, heroImageDomNode }));
}

function removeNonGeoConfiguredBrowseByNeedsModule(moduleDomNode, countryCode) {
  moduleDomNode.querySelectorAll(`${SELECTORS.MODULE_ITEM}${GEO_MODULE_SELECTOR}`).forEach((cardDomNode) => {
    const { conditionsToShow } = cardDomNode.dataset;

    if (
      isGeoConfiguredModule(conditionsToShow) &&
      !isUserCountryInWhitelistedCountriesList(countryCode, conditionsToShow)
    ) {
      cardDomNode.remove();
    }
  });
}

const isNotValidCardCount = (cards) => cards.length !== AVAILABLE_CARD_COUNT;

function setupBrowseByNeedsCards(moduleDomNode, cards) {
  const heroImageDomNode = document.querySelector(HERO_SELECTORS.MODULE_IMAGE_WRAPPER);
  const isDesktop = isDesktopBreakpoint();

  cards.forEach((card) => {
    setupBrowseByNeedsAccordion(card);
    heroImageDomNode.insertAdjacentHTML('beforeend', getImagesHtml(card));

    if (isDesktop) {
      initImageSlider({ moduleDomNode, heroImageDomNode, card });
    }
    showModule(card);
  });
}

export const setupBrowseByNeedsModule = async () => {
  const moduleDomNode = document.querySelector(SELECTORS.MODULE);

  if (!moduleDomNode) {
    return;
  }

  const { countryCode } = await geoData;

  removeNonGeoConfiguredBrowseByNeedsModule(moduleDomNode, countryCode);

  const cards = moduleDomNode.querySelectorAll(SELECTORS.MODULE_ITEM);

  if (isNotValidCardCount(cards)) {
    logWarning(`${ROLLBAR_LOGGING_PREFIX} setupBrowseByNeeds failed: Amount of cards should be 4`, {
      countryCode,
      actualCardsLength: cards.length
    });
    moduleDomNode.remove();
    return;
  }

  setupBrowseByNeedsCards(moduleDomNode, cards);
  setupAnalytics(moduleDomNode);
};

export function setupAnalytics(moduleDomNode) {
  const pageType = document.documentElement.className;

  moduleDomNode.querySelectorAll(SELECTORS.MODULE_ITEM).forEach((card) => {
    setupAccordionTrackingEvent({
      eventKey: 'MODULE_ITEM_ACCORDION_LABEL',
      card,
      pageType
    });
    setupAccordionTrackingEvent({
      eventKey: 'MODULE_ITEM_ACCORDION_LABEL_CLOSE',
      card,
      pageType
    });
    setupCtaLinkTrackingEvent({
      eventKey: 'MODULE_ITEM_LINK',
      card,
      pageType
    });
    setupCtaLinksWithinTextTrackingEvent({
      eventKey: 'MODULE_ITEM_DESCRIPTION_LINK',
      card,
      pageType
    });
    setupHoverTrackingEvent({ card, pageType });
  });
}

function setupAccordionTrackingEvent({ card, pageType, eventKey }) {
  const domNode = card.querySelector(`${SELECTORS[eventKey]}${TRACKING_SELECTORS.CLICK_INTERACTION}`);

  if (domNode) {
    const { clickInteractionTrackingLabel } = domNode.dataset;

    domNode.addEventListener('click', () => {
      trackInteraction({
        label: `${INTERACTION_LABELS[eventKey]} ${clickInteractionTrackingLabel}`,
        page_type: pageType
      });
    });
  }
}

function setupCtaLinkTrackingEvent({ card, pageType, eventKey }) {
  const domNode = card.querySelector(`${SELECTORS[eventKey]}${TRACKING_SELECTORS.CUSTOM_INTERACTION}`);

  if (domNode) {
    const { customTrackingLabel } = domNode.dataset;

    domNode.addEventListener('click', () => {
      makeSegmentTrackCall(EventNameTypes.cta, {
        cta_name: customTrackingLabel,
        page_type: pageType
      });
    });
  }
}

function setupCtaLinksWithinTextTrackingEvent({ card, pageType, eventKey }) {
  card.querySelectorAll(SELECTORS[eventKey]).forEach((domNode) => {
    const { interactionTrackingLabel } = domNode.dataset;

    domNode.addEventListener('click', () => {
      if (interactionTrackingLabel) {
        trackInteraction({
          label: interactionTrackingLabel,
          page_type: pageType
        });
      } else {
        makeSegmentTrackCall(EventNameTypes.cta, {
          cta_name: domNode.textContent,
          page_type: pageType
        });
      }
    });
  });
}

function setupHoverTrackingEvent({ card, pageType }) {
  const { hoverInteractionTrackingLabel } = card.dataset;
  const isDesktop = isDesktopBreakpoint();

  if (!hoverInteractionTrackingLabel || !isDesktop) {
    return;
  }

  let timer;

  card.addEventListener('mouseenter', () => {
    timer = setTimeout(() => {
      trackInteraction({
        page_type: pageType,
        label: `Hover - ${hoverInteractionTrackingLabel}`
      });
    }, 1000);
  });

  card.addEventListener('mouseleave', () => {
    clearTimeout(timer);
  });
}
